import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const DeleteTaskTypeVariablesSchema = z.object({
  id: z.union([z.string(), z.number()]),
});

export type DeleteTaskTypeVariables = z.infer<typeof DeleteTaskTypeVariablesSchema>;

export async function deleteTaskTypeFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: DeleteTaskTypeVariables,
) {
  const parsed = DeleteTaskTypeVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  return instance.delete(`/task_types/${parsed.id}`);
}

export function useDeleteTaskType() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeleteTaskTypeVariables>({
    mutationFn: variables => deleteTaskTypeFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['taskTypes'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    DeleteTaskTypeVariables,
    'deleteTaskType'
  >('deleteTaskType', mutation);
}
